import { isEmpty } from "lodash";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { FaCog, FaSyncAlt } from "react-icons/fa";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { toast } from "wc-toast";
import AuthProvider from "../context/AuthContext";
import BetSlipContext from "../context/BetSlipContext";
import { apiGet, apiPost } from "../Utils/apiFetch";
import apiPath from "../Utils/apiPath";

const DEFAULT_STAKES = [100, 200, 500, 5000, 10000, 25000, 50000, 100000];
const THEME_KEY = "user-site-color-theme";
const THEMES = [
  { id: "blue", name: "Blue", top: "#008acb", bottom: "#2b4050" },
  { id: "gold", name: "Gold", top: "#000000", bottom: "#ffc20a" },
  { id: "teal", name: "Teal", top: "#078b91", bottom: "#10676b" },
  { id: "purple", name: "Purple", top: "#383145", bottom: "#000000" },
  { id: "orange", name: "Mint and orange", top: "#49b9aa", bottom: "#ff851b" },
  { id: "pink", name: "Pink", top: "#e83179", bottom: "#880d4f" },
  { id: "bronze", name: "Bronze and green", top: "#905400", bottom: "#106553" },
  { id: "coral", name: "Coral", top: "#000000", bottom: "#ff5527" },
  { id: "aqua", name: "Aqua", top: "#101313", bottom: "#13aaaa" },
  { id: "lime", name: "Blue and lime", top: "#2c467e", bottom: "#79b831" },
];

const readStoredTheme = () => {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    return THEMES.some((theme) => theme.id === stored) ? stored : "default";
  } catch {
    return "default";
  }
};

// Reusable settings UI (Theme + Stake + Quick Stakes) plus all of its data
// logic. It is rendered inline inside the desktop shell page and inside the
// mobile full-screen overlay, so the two surfaces stay byte-for-byte identical.
export function SettingsBody() {
  let { setSettingToogle, setProfileData, profileData, user } =
    useContext(AuthProvider);
  let { setBetSelectionObj, betSelectionObj, setDefaultStake, setDefaultOdds } =
    useContext(BetSlipContext);
  const [stakedata, setStakeData] = useState(DEFAULT_STAKES);
  const [profileReady, setProfileReady] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(readStoredTheme);
  const [isLoader, setLoader] = useState(false);
  const [binaryChecked, setBinaryChecked] = useState(false);
  const [sportsBookChecked, setSportsBookChecked] = useState(false);
  const [fancyBetChecked, setFancyBetChecked] = useState(false);
  const [oddsChecked, setOddsChecked] = useState(false);
  const [bid, setBid] = useState("");

  const { register, handleSubmit, setValue } = useForm({});

  useEffect(() => {
    const theme = THEMES.find((item) => item.id === selectedTheme);
    const style = document.documentElement.style;
    if (theme) {
      style.setProperty("--site-header-top", theme.top);
      style.setProperty("--site-header-bottom", theme.bottom);
    } else {
      style.removeProperty("--site-header-top");
      style.removeProperty("--site-header-bottom");
    }
  }, [selectedTheme]);

  const selectTheme = (id) => {
    setSelectedTheme(id);
    try {
      localStorage.setItem(THEME_KEY, id);
    } catch {
      // Theme selection still works when browser storage is unavailable.
    }
  };

  const myProfile = async () => {
    setProfileReady(false);
    if (!isEmpty(user)) {
      try {
      const { status, data: response_users } = await apiGet(
        apiPath.userProfile
      );
      if (status === 200) {
        if (response_users.success) {
          setProfileData(response_users?.results);
          setValue("defaultStake", response_users?.results?.defaultStake);
          const savedStakes = response_users?.results?.editStake;
          const legacyDefaults = [10, 20, 40, 60, 100, 200, 500, 1000];
          const hasCustomStakes = Array.isArray(savedStakes) && !(
            savedStakes.length === legacyDefaults.length &&
            savedStakes.every((stake, index) => Number(stake) === legacyDefaults[index])
          );
          // Replace the old API defaults while retaining saved custom amounts.
          setStakeData(DEFAULT_STAKES.map((fallback, index) => {
            const stake = hasCustomStakes ? Number(savedStakes[index]) : NaN;
            return Number.isSafeInteger(stake) && stake > 0 ? stake : fallback;
          }));

          setDefaultStake(response_users?.results?.defaultStake);
          setBid(response_users?.results?.defaultStake);
          setBetSelectionObj({
            ...betSelectionObj,
            bidPrice: response_users?.results?.defaultStake,
            expectany: response_users?.results?.fancyBet,
          });
          setDefaultOdds(response_users?.results?.fancyBet);
          setProfileReady(true);
          setOddsChecked(response_users?.results?.odds);

          setSportsBookChecked(response_users?.results?.sportsBook);
          setFancyBetChecked(response_users?.results?.fancyBet);
          setBinaryChecked(response_users?.results?.binary);
        } else {
          toast.error(response_users.message || "Unable to load settings.");
        }
      } else {
        toast.error("Unable to load settings. Please reopen settings to retry.");
      }
      } catch {
        toast.error("Unable to load settings. Please reopen settings to retry.");
      }
    }
  };
  useEffect(() => {
    myProfile();
  }, [user]);
  const onSubmit = async (request) => {
    if (isLoader || !profileReady) return;
    const stakes = stakedata.map(Number);
    if (stakes.length !== 8 || stakes.some((stake) => !Number.isSafeInteger(stake) || stake <= 0)) {
      toast.error("Enter a positive whole number for each quick stake.");
      return;
    }
    setLoader(true);
      try {
        const { status: stakeStatus, data: stakeResponse } = await apiPost(
          apiPath.userEditStake,
          { editStake: stakes }
        );
        if (stakeStatus !== 200 || !stakeResponse?.success) {
          toast.error(stakeResponse?.message || "Unable to save quick stakes.");
          return;
        }
        const { status, data: response_users } = await apiPost(
          apiPath.userProfileUpdate,
          {
            defaultStake: request.defaultStake,
            fancyBet: fancyBetChecked,
            sportsBook: sportsBookChecked,
            binary: binaryChecked,
            odds: oddsChecked,
            stake: stakes,
            isOneClickBetStake: profileData?.isOneClickBetStake,
            defaultOneClickBetStake: profileData?.defaultOneClickBetStake,
          }
        );
        if (status === 200) {
          if (response_users.success) {
            setLoader(false);
            setDefaultStake(request.defaultStake);
            setBetSelectionObj({
              ...betSelectionObj,
              bidPrice: request.defaultStake,
              expectany: response_users?.results?.fancyBet,
            });
            setDefaultOdds(response_users?.results?.fancyBet);
            setBid(request.defaultStake);
            // setDefaultStake(response_users?.results?.defaultStake);
            setStakeData(stakes);
            setOddsChecked(response_users?.results?.odds);
            setSportsBookChecked(response_users?.results?.sportsBook);
            setFancyBetChecked(response_users?.results?.fancyBet);
            setBinaryChecked(response_users?.results?.binary);
            toast.success(response_users.message);
            // setDropDownShow(false);
            // loginUserDetail();
            await myProfile();
            setSettingToogle(false);
          } else {
            setLoader(false);
            toast.error(response_users.message);
          }
        } else {
          toast.error("Unable to save settings. Please try again.");
        }
      } catch (err) {
        toast.error(err.response?.data?.message || "Unable to save settings. Please try again.");
      } finally {
        setLoader(false);
      }
  };
  const handleStake = (e, index) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setStakeData((stakes) => stakes.map((stake, i) => i === index ? value : stake));
    }
  };

  return (
    <section id="settingSlide" className="settings-panel" aria-labelledby="settings-title">
      <h2 id="settings-title" className="settings-title">
        <FaCog aria-hidden="true" />
        Setting
      </h2>
      <div className="settings-section-bar">
        <h3>Theme</h3>
        <button
          type="button"
          className="settings-theme-reset"
          onClick={() => selectTheme("default")}
          aria-label="Restore default theme"
        >
          Default <FaSyncAlt aria-hidden="true" />
        </button>
      </div>
      <div className="settings-theme-area">
        <div className="settings-theme-grid" role="group" aria-label="Color theme">
          {THEMES.map((theme) => (
            <button
              key={theme.id}
              type="button"
              className="settings-theme-swatch"
              style={{ "--swatch-top": theme.top, "--swatch-bottom": theme.bottom }}
              aria-label={`${theme.name} theme`}
              aria-pressed={selectedTheme === theme.id}
              title={`${theme.name} theme`}
              onClick={() => selectTheme(theme.id)}
            />
          ))}
        </div>
      </div>
      <div className="settings-section-bar">
        <h3>Stake</h3>
      </div>
      <Form onSubmit={handleSubmit(onSubmit)} className="settings-stake-form" aria-busy={isLoader}>
        <input type="hidden" {...register("defaultStake")} />
        <h3 id="quick-stakes-label" className="settings-stakes-label">Quick Stakes</h3>
        <div className="settings-stake-grid" role="group" aria-labelledby="quick-stakes-label">
          {stakedata.map((stake, index) => (
            <input
              key={index}
              id={`stakeEdit_${index + 1}`}
              aria-label={`Quick stake ${index + 1}`}
              type="text"
              inputMode="numeric"
              pattern="[0-9]+"
              required
              autoComplete="off"
              value={stake}
              disabled={isLoader || !profileReady}
              onChange={(event) => handleStake(event, index)}
            />
          ))}
        </div>
        <button
          id="settingSave"
          className="settings-save"
          type="submit"
          disabled={isLoader || !profileReady}
        >
          {isLoader ? "Saving..." : "Save"}
        </button>
      </Form>
    </section>
  );
}

// Mobile / global full-screen overlay. Kept mounted at the app root (via
// All_overlay) so the persisted site theme is applied on load even while the
// panel is closed; the panel itself only renders when settingToogle is true.
function Setting() {
  let { setSettingToogle, settingToogle } = useContext(AuthProvider);
  const location = useLocation();
  const panelRef = useRef(null);

  // Apply the persisted theme once on mount (independent of the interactive
  // swatch state that lives inside SettingsBody).
  useEffect(() => {
    const theme = THEMES.find((item) => item.id === readStoredTheme());
    const style = document.documentElement.style;
    if (theme) {
      style.setProperty("--site-header-top", theme.top);
      style.setProperty("--site-header-bottom", theme.bottom);
    } else {
      style.removeProperty("--site-header-top");
      style.removeProperty("--site-header-bottom");
    }
  }, []);

  useEffect(() => {
    if (!settingToogle) return;
    const header = document.querySelector("#headerMain1, .login-header-new");
    const footer = document.querySelector("nav.cls-footer-sec.sk-footer");
    const updateBounds = () => {
      const bounds = header?.getBoundingClientRect();
      const style = panelRef.current?.style;
      if (!style) return;
      style.setProperty("--settings-top", `${Math.max(0, bounds?.bottom || 0)}px`);
      style.setProperty("--settings-left", `${bounds?.left || 0}px`);
      style.setProperty("--settings-width", `${bounds?.width || window.innerWidth}px`);
      style.setProperty("--settings-footer", `${footer?.getBoundingClientRect().height || 0}px`);
    };
    updateBounds();
    const observer = new ResizeObserver(updateBounds);
    if (header) observer.observe(header);
    if (footer) observer.observe(footer);
    window.addEventListener("resize", updateBounds);
    const previousBodyOverflow = document.body.style.overflow;
    const previousRootOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setSettingToogle(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateBounds);
      document.removeEventListener("keydown", closeOnEscape);
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousRootOverflow;
    };
  }, [settingToogle]);
  useEffect(() => {
    setSettingToogle(false);
  }, [location]);

  if (!settingToogle) return null;

  return (
    <div id="settingDiv" className="settings-page" ref={panelRef}>
      <SettingsBody />
    </div>
  );
}

export default Setting;
